import { useCallback, useEffect, useState } from 'react';
import { Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner, StatusPill, Textarea } from '../components/ui';
import { Dialog } from '../components/dialogs/Dialog';
import { Pagination } from '../components/Pagination';
import { RequestsAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { KnowledgeRequest, KnowledgeRequestStatus, Page } from '../types/api';
import { formatDateTime } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';

interface Filters { status: KnowledgeRequestStatus; }

export function RequestsPage() {
  const [selected, setSelected] = useState<KnowledgeRequest | null>(null);

  const fetcher = useCallback(
    async ({ page, filters }: { page: number; filters: Filters }): Promise<Page<KnowledgeRequest>> => {
      return RequestsAPI.list(filters.status, page);
    },
    [],
  );
  const q = usePagedQuery<KnowledgeRequest, Filters>({ fetcher, initialFilters: { status: 'OPEN' } });

  return (
    <div>
      <PageHeader
        eyebrow="08 — REQUESTS"
        title={<>Knowledge <em>requests</em>.</>}
        lede="User-submitted questions and feedback. Click a row to open the request; reply from the dashboard and the response is pushed to the user via FCM."
        actions={
          <Select
            value={q.filters.status}
            onChange={e => q.setFilters({ status: e.target.value as KnowledgeRequestStatus })}
            style={{ width: 160 }}
          >
            <option value="OPEN">Open</option>
            <option value="REPLIED">Replied</option>
            <option value="ALL">All</option>
          </Select>
        }
      />

      {q.error && <NotImplementedBanner endpoint="/api/admin/requests" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? (
         <Card>
           <EmptyState
             title={q.filters.status === 'REPLIED' ? 'No replied requests' : 'No open requests'}
             hint={q.filters.status === 'OPEN' ? 'Nothing waiting — inbox zero.' : 'Nothing matches this filter.'}
           />
         </Card>
       ) : (
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr><th>From</th><th>Collection</th><th>Question</th><th>Submitted</th><th>Status</th></tr>
            </thead>
            <tbody>
              {q.data.map(r => (
                <tr key={r.id} onClick={() => setSelected(r)} role="link" style={{ cursor: 'pointer' }}>
                  <td className="nb-table__mono">{describeFrom(r)}</td>
                  <td>{r.collectionName || '—'}</td>
                  <td>
                    <div className="nb-table__primary">{snippet(r.content)}</div>
                  </td>
                  <td className="nb-table__mono">{formatDateTime(r.date)}</td>
                  <td><RequestStatusPill replied={r.replied} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      )}

      <RequestDetailDialog
        request={selected}
        onClose={() => setSelected(null)}
        onReplied={() => { setSelected(null); q.refresh(); }}
      />
    </div>
  );
}

function describeFrom(r: KnowledgeRequest): string {
  if (r.email) return r.email;
  if (r.deviceId) return `device:${r.deviceId.slice(0, 8)}…`;
  return '—';
}

function snippet(s?: string): string {
  if (!s) return '—';
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
}

function RequestStatusPill({ replied }: { replied?: boolean }) {
  if (replied) return <StatusPill tone="moss">REPLIED</StatusPill>;
  return <StatusPill tone="ember">OPEN</StatusPill>;
}

function RequestDetailDialog({ request, onClose, onReplied }: {
  request: KnowledgeRequest | null;
  onClose: () => void;
  onReplied: () => void;
}) {
  const [replyTitle, setReplyTitle] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const { run, busy } = useDispatchAction();

  // Reset draft each time a new request is opened.
  useEffect(() => {
    if (request) {
      setReplyTitle(`Re: ${request.collectionName ?? 'your question'}`);
      setReplyContent('');
    }
  }, [request?.id]);

  if (!request) return null;

  const addressable = !!(request.email || request.deviceId);
  const isReplied   = !!request.replied;

  async function sendReply() {
    if (!request) return;
    const ok = await run('Send reply', () => RequestsAPI.reply(request.id, {
      title: replyTitle.trim(),
      content: replyContent.trim(),
    }));
    if (ok !== null) onReplied();
  }

  return (
    <Dialog
      open={!!request}
      onClose={onClose}
      title={isReplied ? 'Knowledge request · replied' : 'Knowledge request'}
      wide
      footer={
        isReplied ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              tone="ember"
              onClick={sendReply}
              disabled={busy || !addressable || !replyTitle.trim() || !replyContent.trim()}
            >
              {busy ? 'Sending…' : 'Send reply'}
            </Button>
          </>
        )
      }
    >
      <div className="nb-form-col">
        <div className="nb-form-row nb-form-row--two">
          <Field label="From"><Input readOnly value={request.email || '—'} /></Field>
          <Field label="Collection"><Input readOnly value={request.collectionName || '—'} /></Field>
        </div>
        <div className="nb-form-row nb-form-row--two">
          <Field label="Device ID"><Input readOnly value={request.deviceId || '—'} /></Field>
          <Field label="Submitted"><Input readOnly value={formatDateTime(request.date) || '—'} /></Field>
        </div>
        <Field label="Question">
          <Textarea readOnly value={request.content || ''} rows={6} />
        </Field>

        {isReplied ? (
          <Card tone="deep">
            <div className="nb-table__primary">Reply sent</div>
            <div className="nb-table__sub">
              by <strong>{request.repliedBy || '—'}</strong>
              {request.repliedAt ? ` · ${formatDateTime(request.repliedAt)}` : ''}
              {request.replyBroadcastId ? ` · broadcast ${request.replyBroadcastId}` : ''}
            </div>
          </Card>
        ) : (
          <>
            {!addressable && (
              <div className="nb-error-banner nb-error-banner--sm">
                This request has no addressable recipient (no email or deviceId). Reply by other means.
              </div>
            )}
            <Field label="Reply title">
              <Input
                value={replyTitle}
                onChange={e => setReplyTitle(e.target.value)}
                placeholder="A short headline the user will see in the push"
              />
            </Field>
            <Field label="Reply message">
              <Textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                rows={8}
                placeholder="Your answer. This goes to the user as an FCM push and appears in their messages inbox."
              />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

